import React, { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  const faqData = [
    {
      id: 1,
      question: "Are all your fragrances 100% authentic?",
      answer: "Yes, absolutely! We guarantee that all our fragrances are 100% authentic. We source our products directly from authorized distributors and manufacturers. Each product comes with authenticity verification, and we stand behind the quality of every fragrance we sell."
    },
    {
      id: 2,
      question: "How long do your fragrances typically last?",
      answer: "The longevity of a fragrance depends on several factors including the concentration (EDT, EDP, Parfum), your skin type, and environmental conditions. Generally, our Eau de Toilettes last 4-6 hours, Eau de Parfums last 6-8 hours, and Parfums can last 8+ hours. We provide detailed information about each fragrance's performance on the product pages."
    },
    {
      id: 3,
      question: "Do you offer free shipping?",
      answer: "Yes! We offer free standard shipping on all orders over $40 within the United States. For orders under $40, standard shipping is $5.99. We also offer expedited shipping options at checkout for faster delivery."
    },
    {
      id: 4,
      question: "What is your return policy?",
      answer: "We accept returns within 30 days of receiving your order. Items must be in their original condition and packaging. A 15% restocking fee applies to opened merchandise. Customers are responsible for return shipping costs unless the return is due to our error."
    },
    {
      id: 5,
      question: "How should I store my fragrances?",
      answer: "Store your fragrances in a cool, dry place away from direct sunlight and heat sources. Avoid storing them in bathrooms where humidity levels fluctuate. Keep bottles upright and tightly closed. Proper storage can significantly extend the life of your fragrance."
    },
    {
      id: 6,
      question: "Can I track my order?",
      answer: "Yes! Once your order ships, you'll receive a tracking number via email. You can use this number to track your package on our website or directly with the shipping carrier. You'll also receive updates on your order status throughout the shipping process."
    },
    {
      id: 7,
      question: "Do you offer gift wrapping?",
      answer: "Yes, we offer beautiful gift wrapping services for a small additional fee. You can select gift wrapping during checkout and even include a personalized message. All our packages are elegantly presented and perfect for gifting."
    },
    {
      id: 8,
      question: "What's the difference between EDT, EDP, and Parfum?",
      answer: "The main difference is the concentration of fragrance oils: Eau de Toilette (EDT) contains 5-15% oils and is lighter, Eau de Parfum (EDP) contains 15-20% oils and lasts longer, and Parfum contains 20-30% oils and is the most concentrated and long-lasting."
    },
    {
      id: 9,
      question: "Do you have a loyalty or rewards program?",
      answer: "Yes! Our Rewards Points Program allows you to earn points with every purchase. You'll earn 1 point for every dollar spent, and you can redeem points for discounts on future orders. Sign up for an account to start earning points immediately."
    },
    {
      id: 10,
      question: "Can I cancel or modify my order?",
      answer: "You can cancel or modify your order within 2 hours of placing it, as long as it hasn't been processed for shipping. Please contact our customer service team immediately at info@giftofgodfragrances.com or call (848) 667-0850 for assistance."
    }
  ];

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".faq-item"),
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Find answers to common questions about our fragrances, shipping, and policies.
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 py-16">
        <div className="space-y-4">
          {faqData.map((faq) => (
            <div key={faq.id} className="faq-item bg-white rounded-xl border border-[#A88006]/20 shadow-lg overflow-hidden">
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-[#A88006]/5 transition-colors"
              >
                <h3 className="text-lg font-semibold text-black pr-4">{faq.question}</h3>
                {openFAQ === faq.id ? (
                  <span className="text-[#A88006] flex-shrink-0 text-xl">▲</span>
                ) : (
                  <span className="text-[#A88006] flex-shrink-0 text-xl">▼</span>
                )}
              </button>
              {openFAQ === faq.id && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
          <h2 className="text-2xl font-bold text-black mb-4">Still Have Questions?</h2>
          <p className="text-gray-700 mb-6">
            Can't find what you're looking for? Our customer service team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@giftofgodfragrances.com"
              className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-all"
            >
              Email Us
            </a>
            <a
              href="tel:+18486670850"
              className="border border-[#A88006] text-[#A88006] font-semibold px-6 py-3 rounded-lg hover:bg-[#A88006]/10 transition-all"
            >
              Call (848) 667-0850
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;